import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { useCallback } from "react";
import type { HcpClassifyJobResult } from "@/types";
import { useAppDispatch, useAppSelector } from "./hooks";
import type { RootState } from "./store";

type HCPCustomer = {
  firstName: string;
  lastName: string;
  email?: string;
  mobileNumber: string;
  homeNumber?: string;
  workNumber?: string;
  company?: string;
  notificationsEnabled?: boolean;
  leadSource?: string;
  notes?: string;
  tags?: string[];
  id: string;
  createdAt: string;
  updatedAt: string;
  companyName?: string;
  companyId: string;
  addresses: Array<{
    street: string;
    streetLine2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
    id?: string;
    type?: string;
  }>;
  doNotService?: boolean;
};

interface HouseCallProState {
  jobType: HcpClassifyJobResult | null;
  customerInfo: HCPCustomer | null;
  showAddCustomerForm: boolean;
}

const initialState: HouseCallProState = {
  jobType: null,
  customerInfo: null,
  showAddCustomerForm: false,
};

const houseCallProSlice = createSlice({
  name: "houseCallPro",
  initialState,
  reducers: {
    setJobType: (state, action: PayloadAction<HcpClassifyJobResult | null>) => {
      state.jobType = action.payload;
    },
    setCustomerInfo: (state, action: PayloadAction<HCPCustomer | null>) => {
      state.customerInfo = action.payload;
    },
    setShowAddCustomerForm: (state, action: PayloadAction<boolean>) => {
      state.showAddCustomerForm = action.payload;
    },
  },
});

const { setJobType, setCustomerInfo, setShowAddCustomerForm } =
  houseCallProSlice.actions;

const selectJobType = (state: RootState) => state.houseCallPro.jobType;
const selectCustomerInfo = (state: RootState) =>
  state.houseCallPro.customerInfo;
const selectShowAddCustomerForm = (state: RootState) =>
  state.houseCallPro.showAddCustomerForm;

export const { reducer } = houseCallProSlice;

export const useHouseCallProIntegration = () => {
  const dispatch = useAppDispatch();
  return {
    jobType: useAppSelector(selectJobType),
    setJobType: useCallback(
      (jobType: HcpClassifyJobResult | null) => dispatch(setJobType(jobType)),
      [dispatch],
    ),
    customerInfo: useAppSelector(selectCustomerInfo),
    setCustomerInfo: useCallback(
      (customerInfo: HCPCustomer | null) =>
        dispatch(setCustomerInfo(customerInfo)),
      [dispatch],
    ),
    showAddCustomerForm: useAppSelector(selectShowAddCustomerForm),
    setShowAddCustomerForm: useCallback(
      (showAddCustomerForm: boolean) =>
        dispatch(setShowAddCustomerForm(showAddCustomerForm)),
      [dispatch],
    ),
  };
};
